/**
 * Prepare a lineup screenshot before sending it to the extract-lineup edge
 * function. Very small screenshots (e.g. a 221px-wide export) make the time
 * digits only a few pixels tall, which the AI then misreads. We upscale such
 * images so the digits are larger, and flag genuinely low-resolution uploads so
 * the UI can nudge the producer to use a sharper screenshot.
 *
 * The decision (`planImageUpscale`) is pure and unit-tested; the canvas-based
 * rendering is a thin wrapper kept out of the testable surface.
 */
import { fileToBase64 } from "./fileToBase64";

/** Below this width the time digits are too small to read reliably → warn. */
export const LOW_RES_WIDTH = 900;
/** Upscale narrow images until they reach roughly this width. */
const TARGET_MIN_WIDTH = 1200;
/** Never enlarge more than this, nor produce an absurdly tall canvas. */
export const MAX_SCALE = 6;
const MAX_HEIGHT = 4000;

export interface UpscalePlan {
  /** True when the source is small enough that the AI may misread it. */
  lowResolution: boolean;
  /** Integer factor to enlarge by (1 = leave as-is). */
  scale: number;
}

/** Decide whether (and how much) to enlarge an image of the given size. */
export function planImageUpscale(width: number, height: number): UpscalePlan {
  if (!width || !height || width < 0 || height < 0) {
    return { lowResolution: false, scale: 1 };
  }
  const lowResolution = width < LOW_RES_WIDTH;
  let scale = width < TARGET_MIN_WIDTH ? Math.ceil(TARGET_MIN_WIDTH / width) : 1;
  scale = Math.min(scale, MAX_SCALE);
  // Keep the enlarged canvas a sane height for tall whole-day timetables.
  while (scale > 1 && height * scale > MAX_HEIGHT) {
    scale -= 1;
  }
  return { lowResolution, scale: Math.max(1, scale) };
}

export interface PreparedImage {
  /** base64 data URL ready for the edge function. */
  dataUrl: string;
  /** True when the original was low resolution (UI should warn). */
  lowResolution: boolean;
}

/**
 * Load `file`, upscale it when small, and return a base64 data URL. Falls back
 * to the raw file bytes if anything in the canvas path fails (so a quirky
 * browser never blocks an upload).
 */
export async function prepareImageForUpload(file: File): Promise<PreparedImage> {
  let loaded: { img: HTMLImageElement; dataUrl: string };
  try {
    loaded = await loadImage(file);
  } catch {
    // Image couldn't be decoded at all — send the original bytes, resolution
    // unknown (no warning).
    return { dataUrl: await fileToBase64(file), lowResolution: false };
  }

  const { img, dataUrl } = loaded;
  const plan = planImageUpscale(img.naturalWidth, img.naturalHeight);
  if (plan.scale === 1) {
    return { dataUrl, lowResolution: plan.lowResolution };
  }
  // If the upscale itself fails (canvas quirk), still send the original AND
  // keep the low-resolution warning — the image is genuinely small.
  let scaled: string | null = null;
  try {
    scaled = upscaleImage(img, plan.scale);
  } catch {
    scaled = null;
  }
  return { dataUrl: scaled ?? dataUrl, lowResolution: plan.lowResolution };
}

function loadImage(file: File): Promise<{ img: HTMLImageElement; dataUrl: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onload = () => resolve({ img, dataUrl });
      img.onerror = () => reject(new Error("decode failed"));
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/** Draw an already-loaded image onto a `scale`× canvas and return a PNG data URL. */
function upscaleImage(img: HTMLImageElement, scale: number): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth * scale;
  canvas.height = img.naturalHeight * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/png");
}
